import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import logo from 'assets/logo-reverse.svg';

const templates = [
  { id: 'startup', name: 'Startup', preview: '/templates/startup.png' },
  { id: 'agency', name: 'Agency', preview: '/templates/agency.png' },
  { id: 'portfolio', name: 'Portfolio', preview: '/templates/portfolio.png' },
  { id: 'blog', name: 'Blog', preview: '/templates/blog.png' },
];

export const StartBuilding = () => {
  const [params] = useSearchParams();
  const plan = params.get('plan') || 'Starter';

  const [step, setStep] = useState(0);

  const [checking, setChecking] = useState(false);
  const [available, setAvailable] = useState(null);

  const [data, setData] = useState({
    name: '',
    bio: '',
    subdomain: '',
    domain: '',
    template: '',
    email: '',
    password: '',
  });

  const planSteps = {
    Starter: ['name', 'bio', 'subdomain', 'template', 'account'],
    Growth: ['name', 'bio', 'subdomain', 'domain', 'template', 'account'],
    Enterprise: ['name', 'bio', 'domain', 'template', 'account'],
  };

  const steps = planSteps[plan];

  const next = () => setStep((s) => Math.min(s + 1, steps.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const update = (field, val) => {
    setData({ ...data, [field]: val });
  };

  useEffect(() => {
    if (steps[step] === 'subdomain' && data.subdomain) {
      setChecking(true);

      const timeout = setTimeout(() => {
        setChecking(false);
        setAvailable(Math.random() > 0.3);
      }, 900);

      return () => clearTimeout(timeout);
    }
  }, [data.subdomain]);

  useEffect(() => {
    const listener = (e) => {
      if (e.key === 'Enter') next();
    };

    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [step]);

  return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">
      <div className="absolute top-10 w-[500px]">
        <div className="h-1 bg-slate-800 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-amber-400"
            animate={{ width: `${((step + 1) / steps.length) * 100}%` }}
          />
        </div>
      </div>

      <div className="max-w-2xl w-full px-6">
        <Link to="/" className="inline-flex items-center gap-2">
          <img src={logo} alt="Ashita Logo" className="h-12 w-auto object-contain" />
        </Link>
        <div className="mb-10 text-sm text-slate-400">
          Plan Selected: <span className="text-amber-400">{plan}</span>
        </div>

        <AnimatePresence mode="wait">
          {steps[step] === 'name' && (
            <Step key="name">
              <Title>What is the name of your website?</Title>

              <Input
                placeholder="Acme Inc"
                value={data.name}
                onChange={(e) => update('name', e.target.value)}
              />
            </Step>
          )}

          {steps[step] === 'bio' && (
            <Step key="bio">
              <Title>Describe your website</Title>

              <textarea
                className="w-full p-4 bg-slate-900 border border-slate-800 rounded-lg"
                rows={4}
                placeholder="We help businesses..."
                value={data.bio}
                onChange={(e) => update('bio', e.target.value)}
              />
            </Step>
          )}

          {steps[step] === 'subdomain' && (
            <Step key="subdomain">
              <Title>Choose your subdomain</Title>

              <div className="flex items-center bg-slate-900 border border-slate-800 rounded-lg">
                <input
                  className="flex-1 p-4 bg-transparent outline-none"
                  placeholder="mywebsite"
                  value={data.subdomain}
                  onChange={(e) => update('subdomain', e.target.value)}
                />

                <span className="px-4 text-slate-400">.ashita.africa</span>
              </div>

              {checking && <p className="text-slate-400 mt-2">Checking availability...</p>}

              {available === true && <p className="text-green-400 mt-2">Available ✓</p>}

              {available === false && <p className="text-red-400 mt-2">Already taken</p>}
            </Step>
          )}

          {steps[step] === 'domain' && (
            <Step key="domain">
              <Title>Add your custom domain</Title>

              <Input
                placeholder="example.com"
                value={data.domain}
                onChange={(e) => update('domain', e.target.value)}
              />
            </Step>
          )}

          {steps[step] === 'template' && (
            <Step key="template">
              <Title>Select a template</Title>

              <div className="grid grid-cols-2 gap-6">
                {templates.map((t) => (
                  <motion.div
                    key={t.id}
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => {
                      update('template', t.id);
                      next();
                    }}
                    className={`cursor-pointer rounded-xl overflow-hidden border ${
                      data.template === t.id ? 'border-amber-400' : 'border-slate-800'
                    }`}
                  >
                    <img src={t.preview} />

                    <div className="p-4 bg-slate-900">{t.name}</div>
                  </motion.div>
                ))}
              </div>
            </Step>
          )}

          {steps[step] === 'account' && (
            <Step key="account">
              <Title>Create your account</Title>

              <button className="w-full p-4 bg-white text-black rounded-lg mb-6">
                Continue with Google
              </button>

              <Input
                placeholder="Email"
                value={data.email}
                onChange={(e) => update('email', e.target.value)}
              />

              <Input
                type="password"
                placeholder="Password"
                value={data.password}
                onChange={(e) => update('password', e.target.value)}
              />

              <button className="w-full mt-6 p-4 bg-amber-400 text-black rounded-lg font-semibold">
                Create Website
              </button>
            </Step>
          )}
        </AnimatePresence>

        <div className="flex justify-between mt-10">
          {step > 0 && (
            <button onClick={prev} className="text-slate-400 hover:text-white">
              ← Back
            </button>
          )}

          {step < steps.length - 1 && (
            <button onClick={next} className="ml-auto bg-amber-400 px-6 py-2 rounded-lg text-black">
              Continue
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const Step = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 50 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -30 }}
    transition={{ duration: 0.35 }}
    className="space-y-6"
  >
    {children}
  </motion.div>
);

const Title = ({ children }) => <h1 className="text-4xl font-bold">{children}</h1>;

const Input = (props) => (
  <input {...props} className="w-full p-4 bg-slate-900 border border-slate-800 rounded-lg" />
);
